import React, {useCallback, useEffect, useState} from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CopyIcon from '@mui/icons-material/FileCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import {format} from 'date-fns';
import {confirm, feedback} from 'shared/services';
import {PermissionsDetailService} from 'shared/services/api/permissions/PermissionsDetail';
import {
  ITokenServico,
  TokenServicoService,
} from 'shared/services/api/reports/TokenServico';

export const TokenServicoSection: React.FC = () => {
  const [tokenServico, setTokenServico] = useState<ITokenServico | null>(
    null,
  );
  // A visibilidade da seção é decidida pelo backend: usuários sem permissão
  // de administrador recebem falha ao consultar o token e a seção some.
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [generating, setGenerating] = useState(false);
  const [visible, setVisible] = useState(false);
  const [geradoPorNome, setGeradoPorNome] = useState('');

  useEffect(() => {
    TokenServicoService.getTokenServico().then((result) => {
      setHasAccess(result.success);
      if (result.success) {
        setTokenServico(result.data);
      }
    });
  }, []);

  useEffect(() => {
    if (!tokenServico?.geradoPor) {
      setGeradoPorNome('');
      return;
    }

    PermissionsDetailService.getById(tokenServico.geradoPor).then((result) => {
      setGeradoPorNome(
        result.success ? result.data.userName : tokenServico.geradoPor,
      );
    });
  }, [tokenServico?.geradoPor]);

  const gerarToken = useCallback(() => {
    setGenerating(true);
    TokenServicoService.gerarTokenServico().then((result) => {
      setGenerating(false);
      if (result.success) {
        setTokenServico(result.data);
        setVisible(false);
        feedback('Token de serviço gerado com sucesso.', 'success');
      } else {
        feedback(
          result.message || 'Erro ao gerar o token de serviço.',
          'error',
        );
      }
    });
  }, []);

  const handleGerarClick = useCallback(() => {
    if (tokenServico) {
      confirm(
        'Atenção: gerar um novo token invalidará o token atual. Integrações ativas que o utilizam deixarão de funcionar imediatamente. Deseja continuar?',
        'warning',
        gerarToken,
      );
    } else {
      gerarToken();
    }
  }, [tokenServico, gerarToken]);

  const copyToken = useCallback(() => {
    if (!tokenServico) return;
    navigator.clipboard.writeText(tokenServico.token);
    feedback('Copiado!', 'success');
  }, [tokenServico]);

  const formattedGeradoEm = tokenServico?.geradoEm
    ? format(new Date(tokenServico.geradoEm), "dd/MM/yyyy 'às' HH'h'mm")
    : '';

  if (!hasAccess) {
    return null;
  }

  return (
    <Box
      width={'100%'}
      maxWidth={'100%'}
      border={'1px solid #B2DFDB'}
      borderRadius={1}
      padding={1.5}
      overflow={'hidden'}
      style={{boxSizing: 'border-box'}}>
      <Box display="flex" alignItems="center" gap={1} marginBottom={1} flexWrap="wrap">
        <VpnKeyIcon color="primary" fontSize="small" />
        <Typography
          color="primary"
          variant="subtitle2"
          style={{
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>
          Token de Serviço
        </Typography>
        <Chip
          label="Somente administradores"
          size="small"
          color="primary"
          style={{height: 20, fontSize: 10}}
        />
      </Box>

      <Box display="flex" alignItems="center" gap={1} width={'100%'} minWidth={0}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGerarClick}
          disabled={generating}
          style={{flexShrink: 0}}
          startIcon={
            generating ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }>
          Gerar
        </Button>

        <TextField
          style={{flex: '1 1 auto', minWidth: 0}}
          fullWidth
          size="small"
          label={'Token de Serviço'}
          variant="outlined"
          type={visible ? 'text' : 'password'}
          value={tokenServico ? tokenServico.token : ''}
          placeholder="Nenhum token de serviço gerado"
          disabled={!tokenServico}
          InputProps={{
            readOnly: true,
          }}
        />

        <Tooltip title={visible ? 'Ocultar token' : 'Mostrar token'}>
          <span>
            <IconButton
              style={{flexShrink: 0}}
              disabled={!tokenServico}
              onClick={() => setVisible((current) => !current)}>
              {visible ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={'Copiar Token de Serviço'}>
          <span>
            <IconButton
              style={{flexShrink: 0}}
              disabled={!tokenServico}
              onClick={copyToken}>
              <CopyIcon color={tokenServico ? 'primary' : 'inherit'} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {tokenServico && (
        <Box marginTop={0.5}>
          <Typography variant="caption" color="textSecondary">
            Gerado em: {formattedGeradoEm} · por {geradoPorNome}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
